/* XENGINEERS 2.5.0. Small, dependency-free behaviours for the phone experience. */
(() => {
  'use strict';
  const html = document.documentElement;
  const reduce = matchMedia('(prefers-reduced-motion: reduce)');
  const ready = (fn) => (document.readyState === 'loading' ? document.addEventListener('DOMContentLoaded', fn, { once: true }) : fn());
  const overlayOpen = () => html.dataset.xeOverlay === 'true' || html.classList.contains('xe-popup-open');

  // An attribute is written only when its value changes: every write invalidates style, even with the same value.
  const flag = (key, value) => { if (html.dataset[key] !== value) html.dataset[key] = value; };

  /* Page position: keeps the contact button out of the first screen and out of the footer, out of the way while a
     phone scrolls down the page, and away while the WhatsApp line of a product page is on screen. */
  function pagePosition() {
    let frame = 0;
    const footer = document.querySelector('.shopify-section-group-footer-group');
    const phone = matchMedia('(max-width: 749px)');
    let lastY = window.scrollY;
    const update = () => {
      frame = 0;
      const y = window.scrollY;
      flag('xeTop', String(y < 220));
      if (footer) flag('xeFooterNear', String(footer.getBoundingClientRect().top < window.innerHeight - 120));
      // Direction of travel on phones, with 6px of slack so a finger at rest does not flip it.
      if (phone.matches) {
        if (y > lastY + 6) { if (y > 220) flag('xeScroll', 'down'); lastY = y; }
        else if (y < lastY - 6) { flag('xeScroll', 'up'); lastY = y; }
      } else lastY = y;
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(update); };
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    update();
    const ask = document.querySelector('#MainContent .product__info-container .xe-product-ask');
    if (ask && window.IntersectionObserver) new IntersectionObserver((entries) => flag('xeAskVisible', String(entries[entries.length - 1].isIntersecting))).observe(ask);
  }

  /* Buy bar: appears once the real button has left the screen and presses that same button. */
  function stickyBuy(bar) {
    const section = bar.dataset.sectionId;
    const info = document.getElementById(`MainProduct-${section}`) || document.querySelector('product-info');
    const submit = document.getElementById(`ProductSubmitButton-${section}`) || info?.querySelector('.product-form__submit');
    const action = bar.querySelector('[data-xe-sticky-action]');
    const label = bar.querySelector('[data-xe-sticky-label]');
    const price = bar.querySelector('[data-xe-sticky-price]');
    if (!info || !submit || !action || !label) return;
    const needsChoice = bar.dataset.needsChoice === 'true';
    const chooseLabel = bar.dataset.labelChoose || 'CHOOSE OPTIONS';
    const picker = info.querySelector('.xe-xbelt-configurator, variant-selects, .product-form__input');
    const footer = document.querySelector('.shopify-section-group-footer-group');
    let frame = 0, shown = false, refocus = false;

    const submitLabel = () => (submit.querySelector('span')?.textContent || submit.textContent || '').trim();
    // The button counts as reached once its top edge has entered the screen.
    const reached = () => submit.getBoundingClientRect().top < window.innerHeight - 8;

    // Runs on every scroll frame: it touches the bar only when something has changed.
    function syncLabel() {
      const pending = needsChoice && !reached() && !submit.disabled;
      const text = pending ? chooseLabel : submitLabel();
      if (label.textContent !== text) label.textContent = text;
      const disabled = submit.disabled && !pending;
      if (action.disabled !== disabled) action.disabled = disabled;
      const busy = submit.classList.contains('loading');
      if (action.classList.contains('is-loading') !== busy) action.classList.toggle('is-loading', busy);
      if (action.getAttribute('aria-busy') !== String(busy)) action.setAttribute('aria-busy', String(busy));
    }
    function syncPrice() {
      if (!price) return;
      const total = info.querySelector('[data-xbelt-total]');
      const box = document.getElementById(`price-${section}`);
      const current = box?.querySelector('.price--on-sale .price-item--sale') || box?.querySelector('.price-item--regular');
      const text = (total?.textContent || current?.textContent || '').trim();
      if (text) price.textContent = text;
    }
    function place() {
      frame = 0;
      const box = submit.getBoundingClientRect();
      const visible = box.bottom > 0 && box.top < window.innerHeight;
      const nearFooter = footer ? footer.getBoundingClientRect().top < window.innerHeight - 40 : false;
      // The bar only exists below 990px (CSS); on wider screens it must not claim the corner or hide the contact button.
      const inUse = getComputedStyle(bar).display !== 'none';
      // The name and price at the head of the page already say what the bar says: it waits until they have scrolled away.
      const head = info.querySelector('.product__title');
      const headVisible = !!head && head.getBoundingClientRect().bottom > 0;
      const next = inUse && !visible && !headVisible && !nearFooter && !overlayOpen() && html.dataset.xeKeyboard !== 'true';
      if (next !== shown) {
        shown = next;
        bar.classList.toggle('is-visible', shown);
        bar.inert = !shown;
        html.dataset.xeSticky = String(shown);
        html.style.setProperty('--xe-sticky-buy', shown ? `${Math.ceil(bar.getBoundingClientRect().height)}px` : '0px');
        // The cart drawer returns focus to the control that opened it. While the drawer was open the bar was inert,
        // so that focus was lost; give it back once the bar is usable again.
        if (shown && refocus) {
          refocus = false;
          // Wait a beat: the bar is still mid-transition from hidden, and a hidden element cannot take focus.
          // On a busy phone one beat may not be enough, so it is tried a few times and stops as soon as it holds.
          const giveBack = (tries) => {
            const focused = document.activeElement;
            if (!shown || overlayOpen() || !(!focused || focused === document.body || focused.closest('cart-drawer'))) return;
            action.focus({ preventScroll: true });
            if (document.activeElement !== action && tries > 0) setTimeout(() => giveBack(tries - 1), 160);
          };
          setTimeout(() => giveBack(4), 90);
        }
      }
      syncLabel();
    }
    const schedule = () => { if (!frame) frame = requestAnimationFrame(place); };

    action.addEventListener('click', () => {
      if (needsChoice && !reached() && picker) {
        picker.scrollIntoView({ behavior: reduce.matches ? 'auto' : 'smooth', block: 'center' });
        // Keyboard and screen reader users land on the options they were sent to.
        picker.querySelector('input:not([type="hidden"]):not([disabled]), select:not([disabled]), button:not([disabled])')?.focus({ preventScroll: true });
        return;
      }
      if (submit.disabled) return;
      refocus = true;
      submit.click();
      // If the form reports a problem, bring the message into view instead of failing silently.
      setTimeout(() => {
        const error = info.querySelector('.product-form__error-message-wrapper:not([hidden]), [data-xbelt-error]:not([hidden])');
        if (error) error.scrollIntoView({ behavior: reduce.matches ? 'auto' : 'smooth', block: 'center' });
      }, 900);
    });

    new MutationObserver(() => { syncLabel(); syncPrice(); }).observe(submit, { attributes: true, childList: true, subtree: true, characterData: true });
    const priceBox = document.getElementById(`price-${section}`);
    if (priceBox) new MutationObserver(syncPrice).observe(priceBox, { childList: true, subtree: true, characterData: true });
    const total = info.querySelector('[data-xbelt-total]');
    if (total) new MutationObserver(syncPrice).observe(total, { childList: true, subtree: true, characterData: true });
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule, { passive: true });
    document.addEventListener('xe:overlay', schedule);
    syncPrice(); place();
  }

  /* Product photos: swipe or use the arrows; one photo is shown at a time, so nothing scrolls sideways. */
  function galleryNav(gallery) {
    if (gallery.dataset.xeNav === 'ready' || !gallery.hasAttribute('data-xe-static-gallery')) return;
    const viewer = gallery.querySelector('[id^="GalleryViewer"]');
    const thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
    const list = viewer?.querySelector('.product__media-list');
    if (!viewer || !thumbs || !list) return;
    const targets = () => [...thumbs.querySelectorAll('[data-target]')].filter((item) => item.offsetParent !== null);
    if (targets().length < 2) return;
    gallery.dataset.xeNav = 'ready';

    const nav = document.createElement('div');
    nav.className = 'xe-gallery-nav quick-add-hidden';
    nav.innerHTML = '<button type="button" data-xe-gallery="-1" aria-label="Previous photo"><span aria-hidden="true">←</span></button><span class="xe-gallery-nav__count" aria-hidden="true"></span><button type="button" data-xe-gallery="1" aria-label="Next photo"><span aria-hidden="true">→</span></button>';
    viewer.after(nav);
    const count = nav.querySelector('.xe-gallery-nav__count');

    const index = () => {
      const items = targets();
      const current = items.findIndex((item) => item.querySelector('button')?.hasAttribute('aria-current'));
      if (current > -1) return current;
      const active = list.querySelector('.product__media-item.is-active')?.dataset.mediaId;
      return Math.max(0, items.findIndex((item) => item.dataset.target === active));
    };
    const paint = () => { const items = targets(); count.textContent = `${index() + 1} / ${items.length}`; nav.hidden = items.length < 2; };
    const go = (step) => {
      const items = targets();
      if (items.length < 2) return;
      const next = items[(index() + step + items.length) % items.length];
      if (typeof gallery.setActiveMedia === 'function') gallery.setActiveMedia(next.dataset.target, false);
      else next.querySelector('button')?.click();
      paint();
    };
    nav.addEventListener('click', (event) => {
      const button = event.target.closest('[data-xe-gallery]');
      if (button) go(Number(button.dataset.xeGallery));
    });

    let startX = 0, startY = 0, tracking = false, swallow = 0;
    list.addEventListener('touchstart', (event) => {
      if (event.touches.length !== 1 || event.target.closest('model-viewer, video, iframe, .deferred-media[loaded]')) { tracking = false; return; }
      tracking = true; startX = event.touches[0].clientX; startY = event.touches[0].clientY;
    }, { passive: true });
    list.addEventListener('touchend', (event) => {
      if (!tracking) return;
      tracking = false;
      const touch = event.changedTouches[0];
      const dx = touch.clientX - startX, dy = touch.clientY - startY;
      if (Math.abs(dx) < 44 || Math.abs(dx) < Math.abs(dy) * 1.4) return;
      swallow = Date.now();
      go(dx < 0 ? 1 : -1);
    }, { passive: true });
    // A swipe must not also open the zoom dialog.
    list.addEventListener('click', (event) => { if (Date.now() - swallow < 450) { event.preventDefault(); event.stopPropagation(); } }, true);

    new MutationObserver(paint).observe(thumbs, { attributes: true, subtree: true, attributeFilter: ['aria-current', 'hidden', 'class'] });
    paint();
  }

  /* Campaign code: Shopify stores an applied code in the discount_code cookie; reflect that wherever the code is offered. */
  function campaignState(scope = document) {
    let saved = '';
    try { saved = decodeURIComponent((document.cookie.split('; ').find((part) => part.startsWith('discount_code=')) || '').split('=')[1] || ''); } catch (_) { saved = ''; }
    if (!saved) return;
    scope.querySelectorAll('[data-xe-campaign-code]').forEach((holder) => {
      if (holder.dataset.xeCampaignCode.toLowerCase() !== saved.toLowerCase() || holder.classList.contains('is-applied')) return;
      holder.classList.add('is-applied');
      const state = holder.matches('[data-xe-campaign-state]') ? holder : holder.querySelector('[data-xe-campaign-state]');
      if (state?.dataset.appliedLabel) state.textContent = state.dataset.appliedLabel;
      if (holder.dataset.appliedAria) holder.setAttribute('aria-label', holder.dataset.appliedAria);
      const link = holder.matches('[data-xe-campaign-link]') ? null : holder.querySelector('[data-xe-campaign-link]');
      if (link) link.hidden = true;
    });
  }

  const galleries = (scope) => { if (window.customElements?.whenDefined) customElements.whenDefined('media-gallery').then(() => scope.querySelectorAll('.template-product #MainContent media-gallery, #MainContent product-info media-gallery').forEach(galleryNav)); };

  function setup(scope = document) {
    scope.querySelectorAll('[data-xe-sticky-buy]').forEach((bar) => { if (!bar.dataset.xeReady) { bar.dataset.xeReady = 'true'; stickyBuy(bar); } });
    galleries(scope);
    // Choosing a colour on products with colour-grouped photos swaps in a new <media-gallery>; give it its controls.
    scope.querySelectorAll('#MainContent product-info').forEach((info) => {
      if (info.dataset.xeGalleryWatch) return;
      info.dataset.xeGalleryWatch = 'true';
      new MutationObserver((records) => {
        if (records.some((record) => [...record.addedNodes].some((node) => node.nodeType === 1 && (node.matches('media-gallery') || node.querySelector?.('media-gallery'))))) galleries(document);
      }).observe(info, { childList: true, subtree: true });
    });
  }

  ready(() => {
    pagePosition(); setup(); campaignState();
    // The cart drawer and cart page replace their own markup after every change.
    const cartRoots = document.querySelectorAll('cart-drawer, cart-items, #main-cart-footer');
    if (cartRoots.length) {
      let queued = 0;
      const observer = new MutationObserver(() => { if (!queued) queued = requestAnimationFrame(() => { queued = 0; campaignState(); }); });
      cartRoots.forEach((root) => observer.observe(root, { childList: true, subtree: true }));
    }
  });
  document.addEventListener('shopify:section:load', (event) => setup(event.target));
  // A gallery that opened in 3D has no photo arrows yet: build them the first time the photos are shown.
  document.addEventListener('xe:media-view', (event) => { if (event.detail?.view === 'photos') requestAnimationFrame(() => galleries(document)); });
})();
